import { useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';

import adminAPI from '@/api/admin';
import Button, { BUTTON_KIND } from '@/components/Button';
import { ContactsListItem } from '@/components/ContactsListItem';
import Layout from '@/components/Layout';
import LoadingAnimation from '@/components/LoadingAnimation';
import Pagination from '@/components/Pagination';
import Search from '@/components/Search';
import Sort from '@/components/Sort';
import MESSAGES from '@/constants/messages-en';

export default function ContactListAdminPage() {
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sort, setSort] = useState('date');
  const [order, setOrder] = useState('desc');
  const [search, setSearch] = useState('');

  const { data: contacts } = useQuery({
    queryKey: ['listContactsAdmin', page, pageSize, sort, order, search],
    queryFn: () => listContacts(),
  });

  async function listContacts(
    newPage: number = page,
    newPageSize: number = pageSize,
    newSort: string = sort,
    newOrder: string = order,
    newSearch: string = search,
  ) {
    const res = await adminAPI.listContacts({
      page: newPage,
      pageSize: newPageSize,
      sort: newSort,
      order: newOrder,
      search: newSearch,
    });

    return res.data;
  }

  async function exportCSV() {
    const response = await adminAPI.exportContactsCSV();
    const blob = new Blob([response.data]);
    const a = document.createElement('a');
    a.href = window.URL.createObjectURL(blob);
    a.download = 'admin_contacts.csv';
    a.click();
  }

  return (
    <Layout pageName="Contacts">
      <div className="pb-8 pt-8 md:pt-0 space-y-4">
        <div className="flex flex-wrap items-end xl:space-y-0 justify-end gap-4">
          <Search
            id={`ContactsList-${page}-${pageSize}-${sort}-${order}-${search}`}
            search={search}
            setSearch={setSearch}
            fetchQuery={searchQuery =>
              listContacts(page, pageSize, sort, order, searchQuery)
            }
          />
          <Sort
            id={`ContactsList-${page}-${pageSize}-${sort}-${order}-${search}`}
            options={[
              {
                sort: 'date',
                order: 'desc',
                label: 'Date created (Newest first)',
              },
              {
                sort: 'date',
                order: 'asc',
                label: 'Date created (Oldest first)',
              },
              {
                sort: 'first_name',
                order: 'asc',
                label: 'Name (A-Z)',
              },
              {
                sort: 'first_name',
                order: 'desc',
                label: 'Name (Z-A)',
              },
            ]}
            sort={sort}
            setSort={setSort}
            order={order}
            setOrder={setOrder}
            fetchQuery={(sortQuery, orderQuery) =>
              listContacts(page, pageSize, sortQuery, orderQuery, search)
            }
          />
          <Button
            kind={BUTTON_KIND.WHITE}
            buttonText="Export via CSV"
            onClick={exportCSV}
          />
        </div>
        {contacts ? (
          contacts.data.length > 0 ? (
            <>
              <div className="border border-solid rounded-lg">
                <table
                  className={clsx('w-full max-w-full table-fixed', {
                    'opacity-40': isPaginationLoading,
                  })}
                >
                  <thead className="bg-gray-100 border-b border-gray-200">
                    <tr className="uppercase text-gray-900">
                      <th
                        scope="col"
                        className="font-medium py-3 px-4 text-left text-sm w-1/3"
                      >
                        Name &amp; email
                      </th>
                      <th
                        scope="col"
                        className="font-medium p-3 text-left text-sm w-1/4"
                      >
                        Position &amp; company
                      </th>
                      <th
                        scope="col"
                        className="font-medium p-3 text-left text-sm w-1/5"
                      >
                        Connected with
                      </th>
                      <th scope="col" className="font-medium px-4 py-3 text-sm">
                        View
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {contacts.data.map((contact, index) => (
                      <ContactsListItem key={index} contact={contact} />
                    ))}
                  </tbody>
                </table>
              </div>
              {isPaginationLoading && (
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                  <LoadingAnimation className="w-16 h-16 mx-auto text-brand-500" />
                </div>
              )}
            </>
          ) : (
            <div className="py-32">
              <h3 className="w-full text-center text-2xl leading-8 text-gray-900 font-medium">
                {MESSAGES.contact.list.empty.heading}
              </h3>
              <p className="w-full text-center mt-2 text-sm leading-5 text-gray-500">
                {MESSAGES.contact.list.empty.description}{' '}
                <a
                  href="https://tapt.io/pages/how-to-use"
                  rel="noreferrer"
                  target="_blank"
                >
                  Learn more.
                </a>
              </p>
            </div>
          )
        ) : (
          <div className="flex flex-col justify-center min-h-screen py-6 bg-white sm:px-6 lg:px-8">
            <div>
              <LoadingAnimation className="w-16 h-16 mx-auto text-brand-500" />
            </div>
          </div>
        )}
      </div>
      <Pagination
        id={`ContactsList-${page}-${pageSize}-${sort}-${order}-${search}`}
        page={page}
        setPage={setPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        fetchQuery={(pageQuery, pageSizeQuery) =>
          listContacts(pageQuery, pageSizeQuery, sort, order, search)
        }
        setIsLoading={setIsPaginationLoading}
      />
    </Layout>
  );
}
